class AITrackingService {
  private isRunning: boolean = false;
  private screenshotInterval: NodeJS.Timeout | null = null;
  private config: any = null;
  private lastScreenshot: Date | null = null;
  private listeners: Array<(status: any) => void> = [];
  private directoryHandle: any = null; // FileSystemDirectoryHandle for File System Access API

  constructor() {
    // Load saved state from localStorage
    this.loadState();
    
    // Handle page visibility changes
    this.setupPageVisibilityHandling();
    
    // Handle page unload/reload
    this.setupPageUnloadHandling();
  }

  private loadState() {
    try {
      const savedState = localStorage.getItem('aiTrackingState');
      if (savedState) {
        const state = JSON.parse(savedState);
        // Don't restore isRunning state - always start fresh
        this.isRunning = false;
        this.config = state.config || null;
        this.lastScreenshot = state.lastScreenshot ? new Date(state.lastScreenshot) : null;
        console.log('Loaded AI tracking state:', { 
          isRunning: this.isRunning, 
          hasConfig: !!this.config, 
          lastScreenshot: this.lastScreenshot 
        });
      }
    } catch (error) {
      console.error('Failed to load AI tracking state:', error);
    }
  }

  private saveState() {
    try {
      const state = {
        isRunning: this.isRunning,
        config: this.config,
        lastScreenshot: this.lastScreenshot?.toISOString()
      };
      localStorage.setItem('aiTrackingState', JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save AI tracking state:', error);
    }
  }

  private notifyListeners() {
    const status = {
      isRunning: this.isRunning,
      lastScreenshot: this.lastScreenshot,
      config: this.config
    };
    this.listeners.forEach(listener => listener(status));
  }

  public subscribe(listener: (status: any) => void) {
    this.listeners.push(listener);
    // Immediately notify with current status
    listener({
      isRunning: this.isRunning,
      lastScreenshot: this.lastScreenshot,
      config: this.config
    });

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  public async start(config: any) {
    // 🛡️ AUTH GUARD: Check if user is authenticated before starting
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('⏸️ AI tracking start blocked: User not authenticated');
      return;
    }
    
    if (this.isRunning) {
      console.log('AI tracking already running');
      return;
    }

    console.log('✅ Starting AI tracking with config (auth verified):', config);
    this.config = config;
    this.isRunning = true;
    this.saveState();
    
    // Notify listeners immediately that we're starting
    this.notifyListeners();

    // Start screenshot capture
    await this.startScreenshotCapture();
    
    // Notify again after starting capture
    this.notifyListeners();
  }

  public stop() {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;
    this.lastScreenshot = null;
    this.stopScreenshotCapture();
    
    // Clean up stream and video element
    if (this.currentStream) {
      this.currentStream.getTracks().forEach(track => track.stop());
      this.currentStream = null;
    }
    
    if (this.video) {
      document.body.removeChild(this.video);
      this.video = null;
    }
    
    this.permissionGranted = false;
    
    this.saveState();
    this.notifyListeners();
  }

  public getStatus() {
    return {
      isRunning: this.isRunning,
      lastScreenshot: this.lastScreenshot,
      config: this.config
    };
  }

  private async startScreenshotCapture() {
    // Clear any existing interval
    this.stopScreenshotCapture();

    if (!this.config || !this.config.enabled) {
      console.log('Screenshot capture not started: config not enabled');
      return;
    }

    // Start capturing screenshots at the specified interval
    const intervalMs = (this.config.screenshotInterval || 5) * 60 * 1000; // Back to minutes
    console.log(`Starting screenshot capture with interval: ${intervalMs}ms (${this.config.screenshotInterval} minutes)`);
    
    this.screenshotInterval = setInterval(async () => {
      console.log('Interval triggered - taking screenshot');
      await this.captureScreenshot();
    }, intervalMs);

    // Take first screenshot immediately
    console.log('Taking initial screenshot');
    await this.captureScreenshot();
  }

  private stopScreenshotCapture() {
    if (this.screenshotInterval) {
      clearInterval(this.screenshotInterval);
      this.screenshotInterval = null;
    }
  }

  private currentStream: MediaStream | null = null;
  private permissionGranted: boolean = false;

  private video: HTMLVideoElement | null = null;

  private async captureScreenshot() {
    try {
      // Check if browser supports screen capture
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        console.warn('Screen capture not supported in this browser');
        this.handleScreenshotError('Browser not supported');
        return;
      }

      // Check if tracking is still running (user might have stopped it)
      if (!this.isRunning) {
        console.log('Tracking stopped, skipping screenshot');
        return;
      }

      // Check if we need to initialize or reinitialize
      const needsInitialization = !this.currentStream || 
                                  !this.permissionGranted || 
                                  !this.video ||
                                  this.currentStream.getVideoTracks().length === 0 ||
                                  this.currentStream.getVideoTracks()[0].readyState === 'ended';

      if (needsInitialization) {
        try {
          console.log('Stream status check:', {
            hasStream: !!this.currentStream,
            permissionGranted: this.permissionGranted,
            hasVideo: !!this.video,
            trackCount: this.currentStream?.getVideoTracks().length || 0,
            trackState: this.currentStream?.getVideoTracks()[0]?.readyState || 'none'
          });
          
          console.log('Requesting screen sharing permission...');
          this.currentStream = await navigator.mediaDevices.getDisplayMedia({
            video: {
              width: { ideal: 1920 },
              height: { ideal: 1080 }
            }
          });
          this.permissionGranted = true;

          // Clean up old video element if exists
          if (this.video) {
            try {
              document.body.removeChild(this.video);
            } catch (e) {
              // Element might not be in DOM
            }
          }

          // Create persistent video element
          this.video = document.createElement('video');
          this.video.srcObject = this.currentStream;
          this.video.muted = true;
          this.video.autoplay = true;
          this.video.style.display = 'none'; // Hide the video element
          this.video.style.position = 'absolute';
          this.video.style.top = '-9999px'; // Move off-screen
          document.body.appendChild(this.video); // Add to DOM for proper functioning

          // Listen for stream end (user stops sharing)
          this.currentStream.getVideoTracks()[0].addEventListener('ended', () => {
            console.log('Screen sharing ended by user - will attempt to restart on next interval');
            this.permissionGranted = false;
            this.currentStream = null;
            if (this.video) {
              try {
                document.body.removeChild(this.video);
              } catch (e) {
                // Element might not be in DOM
              }
              this.video = null;
            }
            // Don't stop tracking, just mark as needing reinitialization
            this.notifyListeners();
          });

          // Wait for video to be ready with timeout
          await Promise.race([
            new Promise<void>((resolve) => {
              if (this.video) {
                this.video.onloadedmetadata = () => {
                  console.log('Video metadata loaded, ready for capture');
                  resolve();
                };
                // Also resolve if video is already ready
                if (this.video.readyState >= 1) {
                  console.log('Video already ready for capture');
                  resolve();
                }
              }
            }),
            new Promise<void>((_, reject) => {
              setTimeout(() => reject(new Error('Video load timeout')), 5000);
            })
          ]);

          console.log('Screen sharing initialized successfully');
        } catch (permissionError) {
          console.log('User cancelled screen sharing or permission denied:', permissionError);
          this.permissionGranted = false;
          // Don't stop tracking completely, just skip this screenshot
          return;
        }
      } else {
        console.log('Using existing stream for screenshot');
      }

      if (this.currentStream && this.permissionGranted && this.video) {
        // Ensure video is playing and has valid dimensions
        if (this.video.videoWidth === 0 || this.video.videoHeight === 0) {
          console.log('Video not ready yet, skipping screenshot');
          return;
        }

        // Create canvas to draw the current video frame
        const canvas = document.createElement('canvas');
        canvas.width = this.video.videoWidth;
        canvas.height = this.video.videoHeight;
        
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Ensure video is playing
          if (this.video.paused) {
            try {
              await this.video.play();
              // Small delay to ensure frame is rendered
              await new Promise(resolve => setTimeout(resolve, 100));
            } catch (playError) {
              console.log('Could not play video, skipping screenshot:', playError);
              return;
            }
          }

          // Draw current frame from video
          ctx.drawImage(this.video, 0, 0);
          
          // Convert canvas to blob
          canvas.toBlob(async (blob) => {
            if (blob && this.config?.saveScreenshots && this.isRunning) {
              try {
                // Create a File object from the blob
                // eslint-disable-next-line no-restricted-syntax
                const timestamp = new Date();
                const filename = `screenshot-${timestamp.getFullYear()}-${String(timestamp.getMonth() + 1).padStart(2, '0')}-${String(timestamp.getDate()).padStart(2, '0')}_${String(timestamp.getHours()).padStart(2, '0')}-${String(timestamp.getMinutes()).padStart(2, '0')}-${String(timestamp.getSeconds()).padStart(2, '0')}.png`;
                const file = new File([blob], filename, {
                  type: 'image/png'
                });

                // Handle different save options
                if (this.config.saveScreenshots === 'local') {
                  // Save locally and create thumbnail for server
                  await this.saveScreenshotLocally(blob, filename);
                  // Also send metadata and thumbnail to server for history display
                  await this.createScreenshotMetadata(blob, filename);
                } else if (this.config.saveScreenshots === 'server') {
                  // Upload to server
                  await this.uploadScreenshotToServer(file);
                }
                // If 'none', we don't save but still update tracking status

                // Update last screenshot time regardless of save method
                // eslint-disable-next-line no-restricted-syntax
                this.lastScreenshot = new Date();
                this.saveState();
                this.notifyListeners();
                
                console.log(`Screenshot captured successfully (save method: ${this.config.saveScreenshots})`);
              } catch (error) {
                console.error('Failed to process screenshot:', error);
              }
            }
          }, 'image/png', 0.8);
        }
      }
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
      this.handleScreenshotError(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  private handleScreenshotError(errorMessage: string) {
    console.log('Handling screenshot error:', errorMessage);
    // Don't immediately stop tracking on errors, just clean up current stream
    this.permissionGranted = false;
    this.currentStream = null;
    if (this.video) {
      try {
        document.body.removeChild(this.video);
      } catch (e) {
        // Element might not be in DOM
      }
      this.video = null;
    }
    // The next interval will attempt to reinitialize
    this.notifyListeners();
  }

  public updateConfig(config: any) {
    const wasRunning = this.isRunning;
    
    this.config = config;
    this.saveState();

    // Restart capture with new config if running
    if (wasRunning) {
      this.startScreenshotCapture();
    }
    
    this.notifyListeners();
  }

  public setDirectoryHandle(handle: any) {
    this.directoryHandle = handle;
    console.log('📁 Directory handle set for AI tracking service:', handle?.name);
  }

  public getDirectoryHandle() {
    return this.directoryHandle;
  }

  private setupPageVisibilityHandling() {
    // Handle page visibility changes (tab switching, minimizing)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        console.log('Page hidden - AI tracking continues in background');
        // REMOVED: No longer pause screenshots when page is hidden
        // Screenshots should continue even when working on other websites
      } else {
        console.log('Page visible - AI tracking continues');
        // Check if we should be running and restart if needed (in case something went wrong)
        if (this.isRunning && !this.screenshotInterval) {
          console.log('Restarting AI tracking after page became visible');
          this.startScreenshotCapture();
        }
      }
    });

    // Handle focus/blur events
    window.addEventListener('focus', () => {
      console.log('Window focused - checking AI tracking state');
      // Check if we should be running and restart if needed
      if (this.isRunning && !this.screenshotInterval) {
        console.log('Restarting AI tracking after window focus');
        this.startScreenshotCapture();
      }
    });

    window.addEventListener('blur', () => {
      console.log('Window blurred - AI tracking continues in background');
      // Keep running - screenshots should continue even when working elsewhere
    });
  }

  private setupPageUnloadHandling() {
    // Handle page unload/reload
    window.addEventListener('beforeunload', () => {
      console.log('Page unloading - saving AI tracking state');
      // Save current state before page unloads
      this.saveState();
      
      // Set a flag to indicate we were running when page unloaded
      if (this.isRunning) {
        localStorage.setItem('aiTrackingWasRunning', 'true');
        // eslint-disable-next-line no-restricted-syntax
        localStorage.setItem('aiTrackingUnloadTime', new Date().toISOString());
      }
    });

    // Handle page load - check if we should resume
    window.addEventListener('load', () => {
      this.checkAndResumeTracking();
    });

    // Also check on DOMContentLoaded in case load already fired
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.checkAndResumeTracking();
      });
    } else {
      // DOM already loaded
      setTimeout(() => this.checkAndResumeTracking(), 100);
    }
  }

  private checkAndResumeTracking() {
    try {
      const wasRunning = localStorage.getItem('aiTrackingWasRunning');
      const unloadTime = localStorage.getItem('aiTrackingUnloadTime');
      
      if (wasRunning === 'true' && unloadTime) {
        // 🛡️ AUTH GUARD: Check if user is authenticated before resuming
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('⏸️ AI tracking resume skipped: User not authenticated');
          // Clean up the resume flags
          localStorage.removeItem('aiTrackingWasRunning');
          localStorage.removeItem('aiTrackingUnloadTime');
          return;
        }
        
        // eslint-disable-next-line no-restricted-syntax
        const unloadDate = new Date(unloadTime);
        // eslint-disable-next-line no-restricted-syntax
        const now = new Date();
        const timeDiff = now.getTime() - unloadDate.getTime();
        
        // If less than 5 minutes since unload, offer to resume
        if (timeDiff < 5 * 60 * 1000) {
          console.log('AI tracking was running before page reload, checking if should resume...');
          
          // Check if we have a valid config
          if (this.config && this.config.enabled) {
            console.log('✅ Resuming AI tracking after page reload (auth verified)');
            this.isRunning = true;
            this.saveState();
            this.notifyListeners();
            
            // Show a notification that tracking resumed
            this.showTrackingResumedNotification();
            
            // Start capture after a short delay to ensure page is fully loaded
            setTimeout(() => {
              this.startScreenshotCapture();
            }, 2000);
          }
        }
        
        // Clean up the resume flags
        localStorage.removeItem('aiTrackingWasRunning');
        localStorage.removeItem('aiTrackingUnloadTime');
      }
    } catch (error) {
      console.error('Error checking tracking resume state:', error);
    }
  }

  private showTrackingResumedNotification() {
    // Create a temporary notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4caf50;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      max-width: 300px;
    `;
    notification.innerHTML = '🟢 AI Tracking resumed automatically';
    
    document.body.appendChild(notification);
    
    // Remove after 3 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 3000);
  }

  private async saveScreenshotLocally(blob: Blob, filename: string) {
    try {
      console.log('💾 Saving screenshot locally:', filename);
      
      // Check if File System Access API is available
      if ('showDirectoryPicker' in window) {
        try {
          // If we don't have a directory handle, request one
          if (!this.directoryHandle) {
            console.log('🗂️ No directory handle, requesting folder permission...');
            // @ts-ignore - File System Access API is not in TypeScript types yet
            this.directoryHandle = await window.showDirectoryPicker({
              mode: 'readwrite',
              startIn: 'documents'
            });
            console.log('✅ Directory permission granted:', this.directoryHandle.name);
            
            // Update config with the selected folder name
            if (this.config) {
              this.config.localSavePath = this.directoryHandle.name;
              this.saveState();
              this.notifyListeners();
            }
          }
          
          // Save file directly to the selected directory
          await this.saveFileToDirectory(blob, filename);
          this.showSaveNotification(`Screenshot saved to ${this.directoryHandle.name}: ${filename}`);
          
        } catch (fsError: any) {
          console.log('File System Access API failed:', fsError);
          
          // If user cancelled or permission denied, fall back to download
          if (fsError.name === 'AbortError') {
            console.log('User cancelled folder selection, falling back to download');
          } else {
            console.log('File System Access API error, falling back to download');
          }
          
          this.directoryHandle = null; // Reset handle on error
          this.downloadScreenshot(blob, filename);
          this.showSaveNotification(`Screenshot downloaded to Downloads: ${filename}`);
        }
      } else {
        // Fallback to browser download for browsers without File System Access API
        console.log('📥 File System Access API not available, using browser download');
        this.downloadScreenshot(blob, filename);
        this.showSaveNotification(`Screenshot downloaded to Downloads: ${filename}`);
      }
    } catch (error) {
      console.error('❌ Failed to save screenshot locally:', error);
      // Final fallback to download
      this.downloadScreenshot(blob, filename);
      this.showSaveNotification(`Screenshot downloaded to Downloads: ${filename}`);
    }
  }

  private async saveFileToDirectory(blob: Blob, filename: string) {
    if (!this.directoryHandle) {
      throw new Error('No directory handle available');
    }

    try {
      // First, verify that we still have permission to the directory
      // @ts-ignore - File System Access API is not in TypeScript types yet
      const permissionStatus = await this.directoryHandle.queryPermission({ mode: 'readwrite' });
      console.log('📁 Directory permission status:', permissionStatus);
      
      if (permissionStatus !== 'granted') {
        console.log('📁 Permission not granted, requesting permission...');
        // @ts-ignore - File System Access API is not in TypeScript types yet
        const newPermission = await this.directoryHandle.requestPermission({ mode: 'readwrite' });
        console.log('📁 New permission status:', newPermission);
        
        if (newPermission !== 'granted') {
          throw new Error('Directory permission denied');
        }
      }

      // Create a file handle in the selected directory
      // @ts-ignore - File System Access API is not in TypeScript types yet
      const fileHandle = await this.directoryHandle.getFileHandle(filename, {
        create: true
      });
      
      // Create a writable stream
      // @ts-ignore - File System Access API is not in TypeScript types yet
      const writable = await fileHandle.createWritable();
      
      // Write the blob to the file
      await writable.write(blob);
      
      // Close the file
      await writable.close();
      
      console.log('✅ File saved successfully to directory:', filename);
    } catch (error) {
      console.error('❌ Failed to save file to directory:', error);
      
      // If permission error, reset directory handle so user can select again
      if (error instanceof Error && (
        error.message.includes('permission') || 
        error.message.includes('denied') ||
        error.name === 'NotAllowedError'
      )) {
        console.log('🔄 Resetting directory handle due to permission error');
        this.directoryHandle = null;
      }
      
      throw error;
    }
  }

  private downloadScreenshot(blob: Blob, filename: string) {
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    // Add to DOM, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up object URL
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }

  private showSaveNotification(message: string) {
    // Create a temporary notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #2196f3;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      max-width: 350px;
    `;
    notification.innerHTML = `💾 ${message}`;
    
    document.body.appendChild(notification);
    
    // Remove after 4 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 4000);
  }

  private async createScreenshotMetadata(blob: Blob, filename: string) {
    try {
      console.log('📊 Creating screenshot metadata for local file:', filename);
      
      // 🛡️ AUTH GUARD: Check if user is authenticated before uploading metadata
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('⏸️ Metadata upload skipped: User not authenticated');
        return;
      }
      
      // Import services dynamically to avoid circular dependencies
      const { apiService } = await import('./api');
      const { metadataService } = await import('./metadataService');
      
      // Collect metadata for AI analysis WITHOUT project matching (let backend AI handle it)
      const metadata = metadataService.getAIOptimizedMetadata();
      console.log('📊 Collected metadata for screenshot (NO project matching):', metadata);
      
      // Create a good quality thumbnail for the server (max 400px width for better quality)
      const thumbnailBlob = await this.createThumbnail(blob, 400);
      const thumbnailFile = new File([thumbnailBlob], `thumb_${filename}`, {
        type: 'image/png'
      });
      
      // Send the thumbnail and metadata to server with local file markers
      const formData = new FormData();
      formData.append('screenshot', thumbnailFile);
      formData.append('metadata', JSON.stringify({
        ...metadata,
        isLocalFile: true,
        localFilename: filename,
        localSavePath: this.config.localSavePath || 'Unknown',
        saveMode: 'local' // Explicit save mode for backend
      }));
      
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3002');
      
      const response = await fetch(`${API_BASE_URL}/ai/screenshots/upload`, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Metadata upload failed');
      }

      console.log('✅ Screenshot metadata created successfully:', data.data);
    } catch (error) {
      console.error('❌ Failed to create screenshot metadata:', error);
      // Don't throw error - this is not critical for local saving
    }
  }

  private async createThumbnail(blob: Blob, maxWidth: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      img.onload = () => {
        // Calculate thumbnail dimensions
        const aspectRatio = img.height / img.width;
        const thumbnailWidth = Math.min(img.width, maxWidth);
        const thumbnailHeight = thumbnailWidth * aspectRatio;
        
        canvas.width = thumbnailWidth;
        canvas.height = thumbnailHeight;
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, thumbnailWidth, thumbnailHeight);
        
        // Convert to blob
        canvas.toBlob((thumbnailBlob) => {
          if (thumbnailBlob) {
            resolve(thumbnailBlob);
          } else {
            reject(new Error('Failed to create thumbnail blob'));
          }
        }, 'image/png', 0.8);
      };
      
      img.onerror = () => reject(new Error('Failed to load image for thumbnail'));
      img.src = URL.createObjectURL(blob);
    });
  }

  private async uploadScreenshotToServer(file: File) {
    try {
      console.log('🚀 Starting screenshot upload to server...');
      
      // 🛡️ AUTH GUARD: Check if user is authenticated before uploading
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('⏸️ Upload skipped: User not authenticated');
        return;
      }
      
      // Import services dynamically to avoid circular dependencies
      const { apiService } = await import('./api');
      const { metadataService } = await import('./metadataService');
      
      console.log('✅ Services imported successfully');
      
      // Collect metadata for AI analysis WITHOUT project matching (let backend AI handle it)
      const metadata = metadataService.getAIOptimizedMetadata();
      console.log('📊 Collected metadata for screenshot (NO project matching):', metadata);
      
      // Mark this as a server upload (not local)
      const serverMetadata = {
        ...metadata,
        isLocalFile: false,
        saveMode: 'server'
      };
      
      console.log('📤 Calling apiService.uploadScreenshotWithMetadata...');
      console.log('📤 File details:', { name: file.name, size: file.size, type: file.type });
      
      // Upload screenshot with metadata
      const uploadResult = await apiService.uploadScreenshotWithMetadata(file, serverMetadata);
      
      console.log('✅ Upload successful! Result:', uploadResult);
      console.log('Screenshot uploaded to server successfully with metadata');
    } catch (uploadError) {
      console.error('❌ Failed to upload screenshot to server:', uploadError);
      console.error('❌ Upload error details:', {
        message: uploadError instanceof Error ? uploadError.message : 'Unknown error',
        stack: uploadError instanceof Error ? uploadError.stack : undefined
      });
      // Don't stop tracking on upload errors, just log them
      throw uploadError;
    }
  }

  // Public method to clear tracking state (for logout)
  public clearTrackingState() {
    console.log('🧹 Clearing AI tracking state after logout');
    
    // Stop tracking if running
    if (this.isRunning) {
      this.stop();
    }
    
    // Clear all localStorage flags
    localStorage.removeItem('aiTrackingState');
    localStorage.removeItem('aiTrackingWasRunning');
    localStorage.removeItem('aiTrackingUnloadTime');
    
    // Reset internal state
    this.config = null;
    this.lastScreenshot = null;
    
    console.log('✅ AI tracking state cleared successfully');
  }
}

// Create singleton instance
export const aiTrackingService = new AITrackingService();
